import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material';
import { FC } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import { useForm, FormProvider } from 'react-hook-form';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { AvatarCombobox } from '@mgm/common/components/base';
import { TMember } from '@mgm/common/types/common.type';
import { CustomRangePicker } from '../../../../components/pickers';
import { useRangePicker } from '../../../../hooks/useCommon';
import { TIntegrationHeader } from '../../type';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getMembers } from '../../../../store/team/selectors';
import { TCreateEditForm } from '../../../../components/form';
import { getTokenDetails } from '../../../../store/authenticate/selectors';

const IntegrationHeader: FC<TIntegrationHeader> = ({
  isFetching,
  onClick,
  setSelectedMembers,
  setDatesRange,
  selectedMembers,
  isExportBtnDisabled,
  handleSubmit,
}) => {
  const theme = useTheme() as TCustomTheme;
  const members = useAppSelector(getMembers);
  const { isMobile } = useCustomTheme(theme);
  const { chosenDates } = useRangePicker();
  const { settings } = useAppSelector(getTokenDetails);
  const { t } = useTranslation(['common', 'payroll']);

  const methods = useForm<TCreateEditForm>({
    mode: 'all',
  });

  return (
    <Grid marginTop={1} alignItems="center" marginBottom={3} container spacing={2} columns={12}>
      <Grid
        display="flex"
        size={{
          sm: 6, md: 4, lg: 4, xs: 12,
        }}
      >
        <Card sx={{
          mr: {
            xs: 0, lg: 2, md: 2, sm: 2,
          },
          boxShadow: 'none',
          width: isMobile ? '100%' : 'auto',
        }}
        >
          <Stack alignItems="center" spacing={2} direction="row">
            <FormProvider {...methods}>
              <CustomRangePicker
                format={settings?.ui?.dateFormat}
                defaultValue={chosenDates}
                setDates={setDatesRange}
              />
            </FormProvider>
          </Stack>
        </Card>
      </Grid>
      <Grid
        display="flex"
        size={{
          sm: 6, md: 4, lg: 4, xs: 12,
        }}
      >
        <AvatarCombobox
          selectedMembers={selectedMembers}
          members={members}
          onChange={setSelectedMembers as (value: Array<TMember>) => void}
          locale={{
            selectMembers: t('selectMembers', { ns: 'payroll' }),
            notFound: t('notFound'),
            noResultsFoundFor: t('noResultsFoundFor'),
            tryCheckingForTypos: t('tryCheckingForTypos'),
          }}
        />
      </Grid>
      {isMobile && (
      <Grid
        justifyContent="end"
        display="flex"
        size={{
          sm: 12, md: 4, lg: 4, xs: 6,
        }}
      >
        <Button
          disabled={isExportBtnDisabled}
          fullWidth
          variant="outlined"
          onClick={handleSubmit}
        >
          {t('export')}
        </Button>
      </Grid>
      )}
      <Grid
        justifyContent="end"
        display="flex"
        size={{
          sm: 12, md: 4, lg: 4, xs: 6,
        }}
      >
        <LoadingButton
          disabled={!selectedMembers.length}
          loading={isFetching}
          variant="contained"
          fullWidth={isMobile}
          onClick={onClick}
        >
          {t('getReport', { ns: 'payroll' })}
        </LoadingButton>
      </Grid>
    </Grid>
  );
};

export {
  IntegrationHeader,
};
